import { useAuth0 } from "@auth0/auth0-react";
import { Navigate } from "react-router-dom";
import Header from "../components/Common/Header";
import LoginButton from "../components/Auth/LoginButton";

function Landing() {
  const { isAuthenticated } = useAuth0();

  // Redirect authenticated users to dashboard
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-vault-100 to-vault-50">
      <Header />

      <main className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center">
          <h1 className="text-balance font-serif text-4xl font-bold tracking-tight text-vault-900 sm:text-5xl lg:text-6xl">
            Your Family History,
            <br />
            <span className="text-vault-600">Preserved Forever</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-vault-600">
            The Family Vault is a private, secure archive for your genealogy
            research. Import your family trees, store documents, photos, and
            recordings, and let AI help you discover connections.
          </p>
          <div className="mt-10 flex items-center justify-center gap-4">
            <LoginButton className="text-lg" />
          </div>
        </div>

        {/* Features Grid */}
        <div className="mt-24 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          <FeatureCard
            icon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="h-6 w-6"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3.75 9.776c.112-.017.227-.026.344-.026h15.812c.117 0 .232.009.344.026m-16.5 0a2.25 2.25 0 00-1.883 2.542l.857 6a2.25 2.25 0 002.227 1.932H19.05a2.25 2.25 0 002.227-1.932l.857-6a2.25 2.25 0 00-1.883-2.542m-16.5 0V6A2.25 2.25 0 016 3.75h3.879a1.5 1.5 0 011.06.44l2.122 2.12a1.5 1.5 0 001.06.44H18A2.25 2.25 0 0120.25 9v.776"
                />
              </svg>
            }
            title="Private & Secure"
            description="Your data stays on your own server. No vendor lock-in, full control over your family's history."
          />
          <FeatureCard
            icon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="h-6 w-6"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
                />
              </svg>
            }
            title="GEDCOM Compatible"
            description="Import and export standard GEDCOM files. Works with Ancestry, MyHeritage, and other genealogy software."
          />
          <FeatureCard
            icon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="h-6 w-6"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z"
                />
              </svg>
            }
            title="AI-Powered Research"
            description="Get intelligent suggestions for research directions, potential duplicates, and missing information."
          />
        </div>
      </main>

      {/* Footer */}
      <footer className="mt-24 border-t border-vault-200 bg-white py-8">
        <div className="mx-auto max-w-7xl px-4 text-center text-sm text-vault-500 sm:px-6 lg:px-8">
          <p>&copy; {new Date().getFullYear()} The Family Vault. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

function FeatureCard({ icon, title, description }: FeatureCardProps) {
  return (
    <div className="card animate-fade-in">
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-vault-100 text-vault-600">
        {icon}
      </div>
      <h3 className="font-serif text-lg font-semibold text-vault-900">
        {title}
      </h3>
      <p className="mt-2 text-vault-600">{description}</p>
    </div>
  );
}

export default Landing;

